import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { Plus, Trash2, Wifi, WifiOff } from 'lucide-react';
import api from '../utils/api';

export const DEFAULT_OP_ROWS = [
  { 항목: '장비운용비', 내용: '소비전력 * 전기세 * 출력시간', 단가: '736', 단위: '식', 수량: '16.1', 비고: '1kWh * 184원' },
  { 항목: '감가상각비', 내용: '구매가 / 수명시간 * 출력시간', 단가: '171', 단위: '식', 수량: '16.1', 비고: '120만원 / 7,000시간' },
  { 항목: '인건비', 내용: '시간당인건비 * 작업시간', 단가: '20000', 단위: '식', 수량: '0.25', 비고: '시간당 20,000원, 10분' },
];

export function mkId() { return `r${Date.now()}_${Math.random().toString(36).slice(2, 6)}`; }

export function calcRowTotal(row) {
  const d = parseFloat(String(row.단가).replace(/,/g, '')) || 0;
  const q = parseFloat(String(row.수량).replace(/,/g, '')) || 0;
  return d * q;
}

export function fmt(n) {
  if (!n && n !== 0) return '';
  return Math.round(n).toLocaleString('ko-KR');
}

export function fmtInput(val) {
  const raw = String(val ?? '').replace(/,/g, '');
  if (raw === '') return '';
  if (raw.includes('.')) {
    const [intPart, decPart] = raw.split('.');
    const n = parseInt(intPart, 10);
    const prefix = isNaN(n) ? intPart : n.toLocaleString('ko-KR');
    return prefix + '.' + (decPart ?? '');
  }
  const n = parseFloat(raw);
  if (isNaN(n)) return val ?? '';
  return n.toLocaleString('ko-KR');
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return '방금';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}분 전`;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${mm}/${dd} ${hh}:${min}`;
}

function mkWsUrl(productId, type) {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const isLocal = ['localhost', '127.0.0.1', '100.116.73.97'].includes(window.location.hostname);
  const host = isLocal ? `${window.location.hostname}:3800` : window.location.host;
  return { url: `${proto}//${host}/yjs`, room: `product-${type}-${productId}` };
}

const NUMERIC_COLS = ['단가', '수량'];

export function RowTable({ rows, onChange, placeholder }) {
  const cols = ['항목', '내용', '단가', '단위', '수량', '비고'];
  const tdCls = 'px-2 py-1.5';
  const inputCls = 'w-full bg-transparent text-sm text-white focus:outline-none placeholder-gray-700';

  const addRow = () => onChange([...rows, { id: mkId(), 항목: '', 내용: '', 단가: '', 단위: '식', 수량: '', 비고: '' }]);
  const delRow = (id) => onChange(rows.filter(r => r.id !== id));
  const editCell = (id, key, val) => onChange(rows.map(r => r.id === id ? { ...r, [key]: val } : r));

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-gray-800">
            {cols.map(c => (
              <th key={c} className={`${tdCls} text-left text-gray-500 font-medium whitespace-nowrap ${
                c === '내용' ? 'w-40' : c === '비고' ? 'w-32' : 'w-20'
              }`}>{c}</th>
            ))}
            <th className={`${tdCls} text-right text-gray-500 font-medium w-24`}>합계</th>
            <th className="w-8" />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={8} className="py-6 text-center text-gray-600 text-sm">{placeholder}</td></tr>
          )}
          {rows.map(row => (
            <tr key={row.id} className="border-b border-gray-800/60 hover:bg-gray-800/30 group">
              {cols.map(col => (
                <td key={col} className={tdCls}>
                  {col === '내용' ? (
                    <textarea rows={1} className={`${inputCls} resize-none leading-snug`}
                      value={row[col]} placeholder="—"
                      onChange={e => editCell(row.id, col, e.target.value)}
                      onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} />
                  ) : NUMERIC_COLS.includes(col) ? (
                    <input className={inputCls}
                      value={fmtInput(row[col])}
                      placeholder={col === '단위' ? '식' : '—'}
                      onChange={e => editCell(row.id, col, e.target.value.replace(/,/g, ''))} />
                  ) : (
                    <input className={inputCls} value={row[col]} placeholder={col === '단위' ? '식' : '—'}
                      onChange={e => editCell(row.id, col, e.target.value)} />
                  )}
                </td>
              ))}
              <td className={`${tdCls} text-right text-gray-300 whitespace-nowrap text-sm`}>
                {calcRowTotal(row) > 0 ? fmt(calcRowTotal(row)) : '—'}
              </td>
              <td className="pr-1">
                <button onClick={() => delRow(row.id)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-gray-600 hover:text-red-400 transition-all">
                  <Trash2 size={11} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={addRow}
        className="mt-2 flex items-center gap-1 text-sm text-gray-600 hover:text-brand px-2 py-1 transition-colors">
        <Plus size={11} /> 행 추가
      </button>
    </div>
  );
}

export default function ProductCosting({ product, onUpdate }) {
  const productId = product.id;
  const productRef = useRef(product);
  useEffect(() => { productRef.current = product; });

  const [mfgRows, setMfgRows] = useState(() => (product.costingData?.mfgRows) || []);
  const [opRows, setOpRows] = useState(() => (product.costingData?.opRows) || DEFAULT_OP_ROWS.map(r => ({ ...r, id: mkId() })));
  const [summary, setSummary] = useState(() => (product.costingData?.summary) || { 재료비: '', 운영비: '', 합계: '', 이윤: '', 총합: '', 플랫폼수수료: '', 소비자가: '' });
  const [connected, setConnected] = useState(false);
  const [peers, setPeers] = useState([]);
  const [saveStatus, setSaveStatus] = useState(null);

  const ymapRef = useRef(null);
  const initDone = useRef(false);
  const saveTimer = useRef(null);
  const saveStatusTimer = useRef(null);
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => { onUpdateRef.current = onUpdate; });

  // Refs for latest state (avoid stale closures in callbacks)
  const mfgRowsRef = useRef(mfgRows);
  const opRowsRef = useRef(opRows);
  const summaryRef = useRef(summary);
  useEffect(() => { mfgRowsRef.current = mfgRows; }, [mfgRows]);
  useEffect(() => { opRowsRef.current = opRows; }, [opRows]);
  useEffect(() => { summaryRef.current = summary; }, [summary]);

  // 자동 계산
  const auto = useMemo(() => {
    const 재료비 = mfgRows.reduce((s, r) => s + calcRowTotal(r), 0);
    const 운영비 = opRows.reduce((s, r) => s + calcRowTotal(r), 0);
    const 합계 = 재료비 + 운영비;
    const 이윤 = 합계;
    const 총합 = 합계 + 이윤;
    const 플랫폼수수료 = 총합 * 0.15;
    const 소비자가 = 총합 + 플랫폼수수료;
    return { 재료비, 운영비, 합계, 이윤, 총합, 플랫폼수수료, 소비자가 };
  }, [mfgRows, opRows]);

  // 핵심: provider를 useEffect 내부에서 생성 → React Strict Mode 호환
  useEffect(() => {
    initDone.current = false;
    const doc = new Y.Doc();
    const ymap = doc.getMap('data');
    ymapRef.current = ymap;

    const token = localStorage.getItem('mfg_token');
    const { url, room } = mkWsUrl(productId, 'costing');
    const prov = new WebsocketProvider(url, room, doc, { params: { token } });

    prov.on('status', ({ status }) => setConnected(status === 'connected'));
    if (prov.wsconnected) setConnected(true);

    const user = JSON.parse(localStorage.getItem('mfg_user') || '{}');
    prov.awareness.setLocalStateField('user', { name: user.name || user.username || '?', color: '#6366f1' });

    const awarenessHandler = () => {
      const states = [];
      prov.awareness.getStates().forEach((state, clientId) => {
        if (clientId !== doc.clientID && state.user) states.push(state.user);
      });
      setPeers(states);
    };
    prov.awareness.on('change', awarenessHandler);

    const tryInit = () => {
      if (initDone.current) return;
      initDone.current = true;
      if (ymap.size === 0) {
        const saved = productRef.current.costingData || {};
        const d = {
          mfgRows: saved.mfgRows || [],
          opRows: saved.opRows || DEFAULT_OP_ROWS.map(r => ({ ...r, id: mkId() })),
          summary: saved.summary || { 재료비: '', 운영비: '', 합계: '', 이윤: '', 총합: '', 플랫폼수수료: '', 소비자가: '' },
        };
        doc.transact(() => {
          ymap.set('mfgRows', JSON.stringify(d.mfgRows));
          ymap.set('opRows', JSON.stringify(d.opRows));
          ymap.set('summary', JSON.stringify(d.summary));
        });
      }
    };

    const onSync = (isSynced) => { if (isSynced) setTimeout(tryInit, 100); };
    prov.on('synced', onSync);
    if (prov.synced) setTimeout(tryInit, 100);

    const ymapObserver = () => {
      const mfgRaw = ymap.get('mfgRows');
      const opRaw = ymap.get('opRows');
      const smRaw = ymap.get('summary');
      try { if (mfgRaw !== undefined) setMfgRows(JSON.parse(mfgRaw)); } catch {}
      try { if (opRaw !== undefined) setOpRows(JSON.parse(opRaw)); } catch {}
      try { if (smRaw !== undefined) setSummary(prev => ({ ...prev, ...JSON.parse(smRaw) })); } catch {}
    };
    ymap.observe(ymapObserver);

    return () => {
      setConnected(false);
      clearTimeout(saveTimer.current);
      clearTimeout(saveStatusTimer.current);
      prov.awareness.off('change', awarenessHandler);
      prov.off('synced', onSync);
      ymap.unobserve(ymapObserver);
      prov.destroy();
      doc.destroy();
    };
  }, [productId]);

  const triggerSave = useCallback((mr, or, sm) => {
    clearTimeout(saveTimer.current);
    clearTimeout(saveStatusTimer.current);
    setSaveStatus('saving');
    saveTimer.current = setTimeout(async () => {
      try {
        const user = JSON.parse(localStorage.getItem('mfg_user') || '{}');
        const costingData = {
          mfgRows: mr, opRows: or, summary: sm,
          _meta: { by: user.name || user.username || '?', at: new Date().toISOString() },
        };
        const res = await api.put(`/products/${productId}`, { costingData });
        onUpdateRef.current(res.data);
        setSaveStatus('saved');
        saveStatusTimer.current = setTimeout(() => setSaveStatus(null), 2000);
      } catch {
        setSaveStatus('error');
      }
    }, 800);
  }, [productId]);

  const updateMfgRows = useCallback((rows) => {
    ymapRef.current?.set('mfgRows', JSON.stringify(rows));
    triggerSave(rows, opRowsRef.current, summaryRef.current);
  }, [triggerSave]);

  const updateOpRows = useCallback((rows) => {
    ymapRef.current?.set('opRows', JSON.stringify(rows));
    triggerSave(mfgRowsRef.current, rows, summaryRef.current);
  }, [triggerSave]);

  const updateSummary = useCallback((key, val) => {
    const next = { ...summaryRef.current, [key]: val };
    ymapRef.current?.set('summary', JSON.stringify(next));
    triggerSave(mfgRowsRef.current, opRowsRef.current, next);
  }, [triggerSave]);

  const sectionCls = 'bg-gray-900 border border-gray-800 rounded-2xl p-4';
  const sumInputCls = 'bg-transparent text-sm text-white focus:outline-none text-right w-36 border-b border-transparent focus:border-gray-600';

  const getSummaryDisplay = (key) => {
    const ov = summary[key];
    if (ov !== '' && ov !== undefined) return fmtInput(ov);
    return fmt(auto[key]);
  };

  const meta = product.costingData?._meta;

  return (
    <div className="flex flex-col gap-4">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {connected
            ? <Wifi size={13} className="text-green-400" />
            : <WifiOff size={13} className="text-gray-600" />}
          <span className="text-[10px] text-gray-500">
            {connected ? `실시간 협업 중${peers.length > 0 ? ` · ${peers.map(p => p.name).join(', ')}` : ''}` : '연결 중...'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {meta?.at && (
            <span className="text-[10px] text-gray-600">
              {meta.by} · {fmtDate(meta.at)}
            </span>
          )}
          <span className="text-[10px] text-gray-500">
            {saveStatus === 'saving' ? '저장 중...' : saveStatus === 'saved' ? '저장됨 ✓' : saveStatus === 'error' ? '저장 실패' : ''}
          </span>
        </div>
      </div>

      {/* 1. 제작비 */}
      <div className={sectionCls}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">1. 제작비</p>
          <p className="text-sm text-gray-400">
            합계 <span className="text-white font-medium">{fmt(mfgRows.reduce((s, r) => s + calcRowTotal(r), 0))}원</span>
          </p>
        </div>
        <RowTable rows={mfgRows} onChange={updateMfgRows} placeholder="+ 행 추가로 제작 비용을 입력하세요" />
      </div>

      {/* 2. 운영비 */}
      <div className={sectionCls}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">2. 운영비</p>
          <p className="text-sm text-gray-400">
            합계 <span className="text-white font-medium">{fmt(opRows.reduce((s, r) => s + calcRowTotal(r), 0))}원</span>
          </p>
        </div>
        <RowTable rows={opRows} onChange={updateOpRows} placeholder="운영 비용을 입력하세요" />
      </div>

      {/* 3. 합계 */}
      <div className={sectionCls}>
        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest mb-3">3. 합계</p>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="py-1.5 text-left text-sm text-gray-500 font-medium w-40">항목</th>
              <th className="py-1.5 text-right text-sm text-gray-500 font-medium">금액 (원)</th>
            </tr>
          </thead>
          <tbody>
            {[
              { key: '재료비', label: '재료비' },
              { key: '운영비', label: '운영비' },
              { key: '합계', label: '합계', bold: true },
              { key: '이윤', label: '이윤 (합계의 100%)' },
              { key: '총합', label: '총합', bold: true },
              { key: '플랫폼수수료', label: '플랫폼 수수료 (15%)' },
              { key: '소비자가', label: '소비자가', bold: true, highlight: true },
            ].map(({ key, label, bold, highlight }) => (
              <tr key={key} className={`border-b border-gray-800/60 ${highlight ? 'bg-brand/5' : ''}`}>
                <td className={`py-2 text-sm ${bold ? 'text-white font-semibold' : 'text-gray-400'}`}>{label}</td>
                <td className="py-2 text-right">
                  <input
                    className={`${sumInputCls} ${bold ? 'text-white font-semibold' : 'text-gray-300'}`}
                    value={getSummaryDisplay(key)}
                    onChange={e => updateSummary(key, e.target.value.replace(/,/g, ''))}
                    placeholder={fmt(auto[key])}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 text-[10px] text-gray-600">금액을 직접 수정하면 자동계산이 해제됩니다. 빈칸으로 두면 자동 계산됩니다.</p>
      </div>
    </div>
  );
}
